import React, { useContext } from 'react';
import Container from 'shared/container';
import Headline from 'shared/headline';
import { layoutContext } from 'context/layout-provider';

import { toPercent } from 'utils/format-number';
import StickyImg from './stickies/img';
import StickyDetails from './stickies/details';
import Genres from './genres';
import BarsChart from './bars-chart';
import PieChart from './pie-graph';
import s from './stats.module.scss';

function Box({ className, children }) {
  return <div className={`${className || ''} ${s.box}`}>{children}</div>;
}

function StatBar({ name, percent, bg, stats }) {
  const chartStats = stats && stats.reduce(
    (acc, curr) => {
      return {
        names: [...acc.names, { name: curr.name, percent: curr.percent.toFixed(0) }],
        bars: [...acc.bars, curr.percent.toFixed(0)]
      };
    },
    {
      names: [],
      bars: []
    }
  );
  return (
    <div className={s.statsBar}>
      <div className={s.column}>
        {chartStats?.names.map(({ name, percent }) => {
          return (
            <div className={s.nameWrap} key={name}>
              <span>{name}</span>
              <span>
                <strong>{`${percent}%`}</strong>
              </span>
            </div>
          );
        })}
      </div>
      <div className={`${s.column} ${s.barBg} `}>
        {chartStats?.bars.map((percent, i) => {
          return (
            <div className={s.bar} key={`${percent}-${i}`}>
              <div style={{ width: `${percent}%` }} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SeparationLine() {
  return (
    <div className={s.separatorWrapper}>
      <div className={s.separator} />
    </div>
  );
}

function StatsSection({
  featuredImage, // TODO: ADD THIS
  stickyDetails,
  realFollowers,
  influencers,
  massFollowers,
  suspiciousAccounts,
  authenticityRate,
  engagementRate,
  maleDemographic,
  femaleDemographic,
  m1317,
  m1824,
  m2534,
  m3544,
  m4554,
  f1317,
  f1824,
  f2534,
  f3544,
  f4554,
  f55,
  m55,
  countries,
  states,
  cities,
  audienceInterests,
  ...props
}) {
  const donutValues = [
    realFollowers,
    influencers,
    massFollowers,
    suspiciousAccounts
  ];
  const barValues = [
    [m1317, f1317],
    [m1824, f1824],
    [m2534, f2534],
    [m3544, f3544],
    [m4554, f4554],
    [f55, m55]
  ].map(([val1, val2]) => ([(val1 * 100).toFixed(0), (val2 * 100).toFixed(0)]));
  const countryStats = countries?.map(({ percent, country }) => ({ percent: percent * 100, name: country && country.name }));
  const usStates = states?.map(({ percent, state }) => ({ percent: percent * 100, name: state?.name }));
  const citiesStats = cities?.map(({ percent, city }) => ({ percent: percent * 100, name: city?.name }));
  const audienceStats = audienceInterests?.map(({ percent, category }) => ({ percent: percent * 100, name: category?.name }));
  return (
    <Container className={s.root}>
      <div className={s.mainWrapper}>
        <div className={`${s.floatLeftHalfContainer} ${s.desktop}`}>
          <div className={s.stickyContainer}>
            <div className={s.stickyInner}>
              <StickyImg img={featuredImage?.id || 'placeholder-dark'} />
              <StickyDetails details={stickyDetails} />
            </div>
          </div>
        </div>
        <div className={s.floatRightHalfContainer}>
          <Headline h3 title html="audience" />
          <Box>
            <div className={s.barsContainer}>
              <div className={s.pieBorder}>
                <PieChart
                  size={200}
                  data={Object.values(donutValues)}
                  colors={['#54C6E0', '#E1D087', '#21272F', '#3795ab']}
                />
              </div>
              <div className={s.donutLegend}>
                <span>
                  <strong>{toPercent(authenticityRate)}</strong>
                  % AUTHENTICITY RATE
                </span>
                <div>
                  <div
                    className={`${s.circle}`}
                    style={{ backgroundColor: '#54C6E0' }}
                  />
                  {`${toPercent(realFollowers)}% REAL PEOPLE`}
                </div>
                <div>
                  <div
                    className={`${s.circle}`}
                    style={{ backgroundColor: '#E1D087' }}
                  />
                  {`${toPercent(influencers)}% INFLUENCERS`}
                </div>
                <div>
                  <div
                    className={`${s.circle}`}
                    style={{ backgroundColor: '#21272F' }}
                  />
                  {`${toPercent(massFollowers)}% MASS FOLLOWERS`}
                </div>
                <div>
                  <div
                    className={`${s.circle}`}
                    style={{ backgroundColor: '#3795ab' }}
                  />
                  {`${toPercent(suspiciousAccounts)}% SUSPICIOUS ACCOUNTS`}
                </div>
                <span>
                  <strong>{toPercent(engagementRate)}</strong>
                   % ENGAGEMENT RATE
                </span>
              </div>
            </div>
          </Box>
          <Headline h3 title html="demographic" />
          <Box className={s.demographics}>
            <Genres male={maleDemographic * 100} female={femaleDemographic * 100} />
            <div className={`${s.barsContainer} ${s.demographic}`}>
              <div
                className={`${s.demographicTitle} ${s.marginBottom} ${s.noMobile}`}
              >
                <strong>Age</strong>
              </div>
              <BarsChart
                data={Object.values(barValues).map(values =>
                  Object.values(values))}
                labels={['13-17', '18-24', '25-34', '35-44', '45-54', '55+']}
              />
            </div>
            {countries?.length > 0 && (
              <div className={s.demographicTitle}>
                <strong>country</strong>
              </div>
            )}
            {countries?.length > 0 && <StatBar bg stats={countryStats} />}
            {states?.length > 0 && (
              <div className={s.demographicTitle}>
                <strong>US STATES</strong>
              </div>
            )}
            {states?.length > 0 && (
              <StatBar bg stats={usStates} />
            )}
            {cities?.length > 0 && (
              <div className={s.demographicTitle}>
                <strong>CITY</strong>
              </div>
            )}
            {cities?.length > 0 && <StatBar stats={citiesStats} />}
          </Box>
          <Headline h3 title html="audience interests" />
          <Box>
            <StatBar bg stats={audienceStats} />
          </Box>
        </div>
      </div>
    </Container>
  );
}
export default StatsSection;
